import { CheckCircleTwoTone, ExclamationCircleTwoTone } from '@ant-design5/icons';
import { Input, Popover, Segmented, Select, Table, Typography } from 'antd5';
import { useCallback, useMemo, useState } from 'react';
import { useTheme } from 'styled-components';

import { Button, Modal, ModalActionButtons } from 'components/ui';
import { STEPS } from 'horizon/routes/Inspections/InspectionDetail/InspectionDamageIngestion/ModalContent/common';
import {
  ValidationErrorPopoverContent,
  ValidationPaginationHelpText,
  ValidationSummary,
  ValidationTableContainer,
  ValidationToolbar,
} from 'horizon/routes/Inspections/InspectionDetail/InspectionDamageIngestion/ModalContent/common.style';
import {
  type DamageSchemaForValidation,
  getDamageSchemaStringOptions,
  validateCsvDamageRows,
} from 'horizon/routes/Inspections/InspectionDetail/InspectionDamageIngestion/ModalContent/validation';
import {
  type DataField,
  type EditableCsvDamageRow,
  type HeaderMappingEntry,
} from 'horizon/routes/Inspections/InspectionDetail/InspectionDamageIngestion/types';

type ConfirmationProps = {
  dataFields: DataField[];
  headerMapping: HeaderMappingEntry[];
  rows: EditableCsvDamageRow[];
  setRows: (rows: EditableCsvDamageRow[]) => void;
  schema?: DamageSchemaForValidation;
  setStep: (step: number) => void;
  handleSubmit: () => void;
  isSubmitting?: boolean;
};

const DEFAULT_PAGE_SIZE = 25;
const TABLE_SCROLL_Y = 'calc(75vh - 330px)';
const BULK_DAMAGE_CREATION_GUIDE_URL =
  'https://guide.skyspecs.com/horizon/-MPobGVTLxgvA9kxtck7/inspections/third-party-ingestion/bulk-damage-creation';
const { Text } = Typography;

type BulkUpdatePrompt = {
  fieldLabel: string;
  fileHeader: string;
  oldValue: string;
  replacementValue: string;
  rowIds: string[];
};

export const Confirmation = ({
  dataFields,
  headerMapping,
  rows,
  setRows,
  schema,
  setStep,
  handleSubmit,
  isSubmitting = false,
}: ConfirmationProps) => {
  const theme = useTheme();
  const [filterMode, setFilterMode] = useState<string>('all');
  const [bulkUpdatePrompt, setBulkUpdatePrompt] = useState<BulkUpdatePrompt | null>(null);
  const invalidCount = useMemo(() => rows.filter(row => !row.validation.isValid).length, [rows]);
  const hasInvalidRows = invalidCount > 0;
  const visibleRows = useMemo(
    () => (filterMode === 'invalid' ? rows.filter(row => !row.validation.isValid) : rows),
    [filterMode, rows]
  );

  const validateRows = useCallback(
    (nextRows: EditableCsvDamageRow[]) =>
      validateCsvDamageRows({
        rows: nextRows,
        dataFields,
        headerMapping,
        schema,
      }),
    [dataFields, headerMapping, schema]
  );

  const updateRowsForCellValue = useCallback(
    (rowIds: string[], fileHeader: string, value: string) => {
      const rowIdSet = new Set(rowIds);

      setRows(
        validateRows(
          rows.map(currentRow =>
            rowIdSet.has(currentRow.id)
              ? {
                  ...currentRow,
                  values: {
                    ...currentRow.values,
                    [fileHeader]: value,
                  },
                }
              : currentRow
          )
        )
      );
    },
    [rows, setRows, validateRows]
  );

  const handleCellChange = useCallback(
    (row: EditableCsvDamageRow, fileHeader: string, value: string) => {
      updateRowsForCellValue([row.id], fileHeader, value);
    },
    [updateRowsForCellValue]
  );

  const handleSelectChange = useCallback(
    ({
      fieldLabel,
      fileHeader,
      row,
      value,
      validOptions,
    }: {
      fieldLabel: string;
      fileHeader: string;
      row: EditableCsvDamageRow;
      value: string;
      validOptions: string[];
    }) => {
      const oldValue = row.values[fileHeader] ?? '';
      const wasInvalid = oldValue !== '' && !validOptions.includes(oldValue);
      const isValidSelection = value !== '' && validOptions.includes(value);

      updateRowsForCellValue([row.id], fileHeader, value);

      if (!oldValue || !wasInvalid || !isValidSelection) {
        return;
      }

      const matchingRows = rows.filter(
        currentRow => currentRow.id !== row.id && currentRow.values[fileHeader] === oldValue
      );

      if (!matchingRows.length) {
        return;
      }

      setBulkUpdatePrompt({
        fieldLabel,
        fileHeader,
        oldValue,
        replacementValue: value,
        rowIds: [row.id, ...matchingRows.map(matchingRow => matchingRow.id)],
      });
    },
    [rows, updateRowsForCellValue]
  );

  const handleConfirmBulkUpdate = useCallback(() => {
    if (!bulkUpdatePrompt) {
      return;
    }

    updateRowsForCellValue(
      bulkUpdatePrompt.rowIds,
      bulkUpdatePrompt.fileHeader,
      bulkUpdatePrompt.replacementValue
    );
    setBulkUpdatePrompt(null);
  }, [bulkUpdatePrompt, updateRowsForCellValue]);

  const handleCancelBulkUpdate = useCallback(() => setBulkUpdatePrompt(null), []);

  const columns = useMemo(
    () => [
      {
        title: 'Row',
        dataIndex: 'originalIndex',
        fixed: 'left' as const,
        width: 72,
        render: (originalIndex: number) => originalIndex + 2,
      },
      ...headerMapping.map(({ fileHeader, schemaFieldId }) => {
        const fieldLabel = dataFields.find(({ key }) => key === schemaFieldId)?.label ?? fileHeader;

        return {
          title: fieldLabel,
          dataIndex: ['values', fileHeader],
          width: 220,
          onCell: (row: EditableCsvDamageRow) => ({
            className:
              (row.validation.cellErrors[schemaFieldId] ?? []).length > 0
                ? 'csv-damage-invalid-cell'
                : undefined,
          }),
          render: (_value: string, row: EditableCsvDamageRow) => {
            const cellErrors = row.validation.cellErrors[schemaFieldId] ?? [];
            const hasError = cellErrors.length > 0;
            const stringOptions = getDamageSchemaStringOptions(schema, schemaFieldId);
            const editor = (
              <>
                {stringOptions ? (
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    popupMatchSelectWidth={false}
                    aria-label={fieldLabel}
                    value={row.values[fileHeader] || undefined}
                    status={hasError ? 'error' : undefined}
                    options={stringOptions.map(option => ({ label: option, value: option }))}
                    onChange={value =>
                      handleSelectChange({
                        fieldLabel,
                        fileHeader,
                        row,
                        value: value ?? '',
                        validOptions: stringOptions,
                      })
                    }
                    style={{ width: '100%' }}
                  />
                ) : (
                  <Input
                    aria-label={fieldLabel}
                    value={row.values[fileHeader] ?? ''}
                    status={hasError ? 'error' : undefined}
                    onChange={event => handleCellChange(row, fileHeader, event.target.value)}
                  />
                )}
              </>
            );

            return hasError && !stringOptions ? (
              <Popover
                content={
                  <ValidationErrorPopoverContent>
                    {cellErrors.map(error => (
                      <li key={error}>{error}</li>
                    ))}
                  </ValidationErrorPopoverContent>
                }
                title="Invalid value"
              >
                {editor}
              </Popover>
            ) : (
              editor
            );
          },
        };
      }),
    ],
    [dataFields, handleCellChange, handleSelectChange, headerMapping, schema]
  );

  return (
    <>
      <ValidationToolbar>
        <Segmented
          value={filterMode}
          onChange={value => setFilterMode(String(value))}
          options={[
            { label: `All (${rows.length})`, value: 'all' },
            {
              label: (
                <Text type={hasInvalidRows ? 'danger' : 'success'} strong>
                  Invalid ({invalidCount})
                </Text>
              ),
              value: 'invalid',
            },
          ]}
        />
        <ValidationSummary>
          {hasInvalidRows ? (
            <>
              <ExclamationCircleTwoTone twoToneColor={theme.red} />
              <Text type="danger" strong>
                {`${invalidCount} row${invalidCount === 1 ? '' : 's'} must be fixed before upload.`}
              </Text>
            </>
          ) : (
            <>
              <CheckCircleTwoTone twoToneColor={theme.green} />
              <Text type="success" strong>
                Ready to upload!
              </Text>
            </>
          )}
        </ValidationSummary>
      </ValidationToolbar>
      <ValidationTableContainer>
        <Table
          columns={columns}
          dataSource={visibleRows}
          pagination={{
            defaultPageSize: DEFAULT_PAGE_SIZE,
            showSizeChanger: true,
            showTotal: () => (
              <ValidationPaginationHelpText>
                Horizon User Guide:{' '}
                <a href={BULK_DAMAGE_CREATION_GUIDE_URL} rel="noopener noreferrer" target="_blank">
                  Bulk Damage Creation
                </a>
                . For assistance, please contact{' '}
                <a href="mailto:support@skyspecs.com">support@skyspecs.com</a>.
              </ValidationPaginationHelpText>
            ),
          }}
          rowKey="id"
          scroll={{ x: 'max-content', y: TABLE_SCROLL_Y }}
          size="small"
        />
      </ValidationTableContainer>
      <ModalActionButtons>
        <Button
          onClick={() => {
            setStep(STEPS.mapping);
          }}
        >
          Back
        </Button>
        <Button
          type="primary"
          onClick={handleSubmit}
          disabled={isSubmitting || invalidCount > 0}
          loading={isSubmitting}
        >
          Create
        </Button>
      </ModalActionButtons>
      <Modal
        open={!!bulkUpdatePrompt}
        title={
          bulkUpdatePrompt
            ? `Update ${bulkUpdatePrompt.rowIds.length - 1} other instance${
                bulkUpdatePrompt.rowIds.length - 1 === 1 ? '' : 's'
              } of "${bulkUpdatePrompt.oldValue}"?`
            : ''
        }
        okText="Yes"
        cancelText="No"
        onOk={handleConfirmBulkUpdate}
        onCancel={handleCancelBulkUpdate}
        destroyOnHidden
      >
        {bulkUpdatePrompt
          ? `Replace "${bulkUpdatePrompt.oldValue}" with "${bulkUpdatePrompt.replacementValue}" for ${bulkUpdatePrompt.fieldLabel}.`
          : null}
      </Modal>
    </>
  );
};
